import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";

import Button from "@ui-kit/form/Button/Button.web";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import usePublicProductsService from "@app-contexts/public/features/products/data/services/usePublicProductsService";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Heading from "@ui-kit/typography/Heading/Heading.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import React, { useEffect } from "react";
import ItemQuantitySelectInfo from "@ui-application/data-display/ItemQuantitySelectInfo/ItemQuantitySelectInfo.web";
import ProductTypesContainer from "./ProductTypesContainer";

export enum ProductState {
  LOADING = "LOADING",
  EMPTY = "EMPTY",
  HAS_PRODUCTS = "HAS_PRODUCTS",
}

export interface ProductCategoriesProps {
  categoryId: number;
  categoryName: string;
  eventData: PublicBaseEventSerializer;
  onStateChange: (categoryId: number, productState: ProductState) => void;
}

const ProductCategories = ({
  categoryId,
  categoryName,
  eventData,
  onStateChange,
}: ProductCategoriesProps) => {
  const { t } = useTranslation("purchases");

  const productParams = {
    page_size: 3,
    event: eventData?.id,
    category: categoryId,
    event_page: true,
    fee_structure: eventData?.fee_structure,
    payment_type: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
    purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
  };

  const { usePaginated } = usePublicProductsService();
  const {
    isLoading,
    hasNextPage,
    fetchNextPage,
    data: productsData,
    isFetchingNextPage,
  } = usePaginated({
    params: productParams,
    options: {
      enabled: true, // hook will always be enabled
    },
  });

  // Since products can be excluded on an event basis, if we get an empty category,
  // dont render anything.
  const hasNoProducts = productsData?.pages[0]?.results.length === 0;

  useEffect(() => {
    if (isLoading) {
      onStateChange(categoryId, ProductState.LOADING);
    } else if (hasNoProducts) {
      onStateChange(categoryId, ProductState.EMPTY);
    } else {
      onStateChange(categoryId, ProductState.HAS_PRODUCTS);
    }
  }, [hasNoProducts, isLoading, onStateChange, categoryId]);

  return (
    <Flex w="full" direction="column">
      <Heading
        size="2xl"
        p={{ base: "0px 16px 16px 16px", sm: "0px 0px 16px 0px" }}
      >
        {t(`${categoryName}`)}
      </Heading>

      <Flex gap="md" direction="column" mb="xl">
        {/* Display loaded list of product cards */}
        {productsData?.pages?.map((page) =>
          page?.results?.map((product) => (
            <ProductTypesContainer
              key={product.id}
              event={eventData}
              product={product}
              isLoading={false} // keeps the already-loaded product data visible
              hasAllInPricing={Boolean(
                eventData?.venue?.all_in_pricing_is_enabled
              )}
            />
          ))
        )}

        {/* Display loading skeleton */}
        {(isLoading || isFetchingNextPage) && (
          <DetailCardContainer>
            <ItemQuantitySelectInfo
              currency="CAD"
              isLoading={isLoading || isFetchingNextPage}
              amountSelected={0}
              onAmountSelectedChange={undefined}
            />
          </DetailCardContainer>
        )}

        {/* Display Load More button */}
        {hasNextPage && (
          <Flex
            width="100%"
            direction="column"
            alignItems="center"
            px={{ base: "md", sm: "none" }}
          >
            <Button
              data-testid="product-categories-load-more"
              colorPalette="primary"
              variant="solid"
              isLoading={isFetchingNextPage}
              onClick={() => fetchNextPage()}
              mt="none"
              w="full"
            >
              <Text fontSize={{ base: "xs", sm: "md" }}>
                {`${t("Load More")} ${t(`${categoryName}`)}`}
              </Text>
            </Button>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ProductCategories;
